import { Button, Label, Tooltip } from '@vibe/core';
import { Settings } from '@vibe/icons';
import { type SectionDescriptor } from '@gorilla/portal-shared/src/types';
import { type Section as SectionType, type PortalSettings, type BoardSourceFieldValue } from '@gorilla/portal-shared/src/schemas';
import { boardSourceDescriptors } from './board-sources/index';

type BaseProps = {
  section: SectionType;
  sectionDescriptor: SectionDescriptor;
  portalSettings: PortalSettings;
  fieldValues: any;
  screenMode: 'small' | 'medium' | 'large';
  hasErrors: boolean;
  onEditBoardSourceClick: (boardSourceId: string) => void;
};

function useSectionLinkedToMeta({
  section,
  sectionDescriptor,
  portalSettings,
  fieldValues,
}: Pick<BaseProps, 'section' | 'sectionDescriptor' | 'portalSettings' | 'fieldValues'>) {
  const boardSourceValue = fieldValues?.['board-source'] as BoardSourceFieldValue | undefined;

  const linkedBoardSource =
    boardSourceValue && 'boardSourceId' in boardSourceValue && boardSourceValue.boardSourceId
      ? portalSettings.boardSources?.find((bs) => bs.id === boardSourceValue.boardSourceId)
      : undefined;

  const isClientItemSummary = sectionDescriptor.type === 'client-item-summary';

  const effectiveType = isClientItemSummary ? null : (boardSourceValue?.type ?? linkedBoardSource?.type);

  const boardSourceDescriptor = effectiveType ? boardSourceDescriptors.find((d) => d.type === effectiveType) : undefined;

  let boardSourceLabel = '';

  if (section.type === 'item-grid' || section.type === 'item-table') {
    if (!boardSourceValue) {
      boardSourceLabel = '';
    } else if (boardSourceValue.type === 'subitems') {
      boardSourceLabel = 'Linked to Clients Board';
    } else if (linkedBoardSource) {
      boardSourceLabel = `Linked to ${linkedBoardSource.name}`;
    } else if ('boardSourceId' in boardSourceValue && boardSourceValue.boardSourceId) {
      boardSourceLabel = 'Linked board source not found';
    }
  } else if (isClientItemSummary) {
    boardSourceLabel = 'Linked to Clients Board';
  }

  const nameText = isClientItemSummary || boardSourceValue?.type === 'subitems' ? 'Clients Board' : boardSourceDescriptor?.name;

  const descriptionText = isClientItemSummary
    ? 'Show each client their own item columns from the Clients Board'
    : boardSourceDescriptor?.description;

  const canEdit =
    !isClientItemSummary &&
    boardSourceValue?.type !== 'subitems' &&
    !!(boardSourceValue && 'boardSourceId' in boardSourceValue && boardSourceValue.boardSourceId);

  return {
    boardSourceValue,
    linkedBoardSource,
    boardSourceDescriptor,
    isClientItemSummary,
    boardSourceLabel,
    nameText,
    descriptionText,
    canEdit,
  };
}

export function SectionLinkedToText(props: BaseProps) {
  const { boardSourceLabel, nameText, descriptionText } = useSectionLinkedToMeta(props);

  if (!boardSourceLabel) return null;

  return (
    <>
      {nameText && (
        <Tooltip content={descriptionText} position="right" showOnDialogEnter={false}>
          <Label text={nameText} size="medium" kind="line" color="dark" />
        </Tooltip>
      )}
    </>
  );
}

export function SectionLinkedToButton(props: BaseProps) {
  const { boardSourceLabel, canEdit, boardSourceValue } = useSectionLinkedToMeta(props);

  if (!boardSourceLabel) return null;

  return (
    <>
      {canEdit ? (
        <Button
          leftIcon={Settings}
          kind="tertiary"
          size="small"
          color={props.hasErrors ? 'negative' : 'primary'}
          style={{ paddingRight: '8px' }}
          onClick={() => {
            if (!canEdit) return;
            props.onEditBoardSourceClick(boardSourceValue!.boardSourceId!);
          }}
        >
          {boardSourceLabel}
        </Button>
      ) : (
        <div
          style={{
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          <Button
            kind="tertiary"
            size="small"
            style={{ paddingRight: '8px' }}
            onClick={() => {
              // Do nothing
            }}
          >
            {boardSourceLabel}
          </Button>
        </div>
      )}
    </>
  );
}
